import {
  SiteFilterInputObject,
  SiteLocationsQueryVariables,
  useSiteLocationsQuery,
  LocationMapParamsInputObject,
  GeojsonFeatureCollection,
  FilterGroupingEnum,
} from 'graphql-common';
import _get from 'lodash/get';
import _pick from 'lodash/pick';
import createSearchFilters from '@lib/utils/createSearchFilters';
import { GeojsonFeatureKind } from 'routes/Dashboard/Monitoring/enums';
import { ListQueryHookProps, ListQueryHookResult } from '../types';

type Args = {
  additionalSiteFilters?: object[];
  mapParams: LocationMapParamsInputObject | null;
};

export default function useSiteLocationsQueryHook({
  additionalSiteFilters,
  searchQuery,
  skip,
  mapParams,
  usePrevious = true,
  fetchPolicy = 'network-only',
}: ListQueryHookProps & Args): ListQueryHookResult<GeojsonFeatureCollection> {
  const sitesQueryVariables: SiteLocationsQueryVariables = {
    mapParams: mapParams
      ? _pick(mapParams, ['maxLat', 'maxLon', 'minLat', 'minLon', 'zoom'])
      : {
          maxLat: 0,
          maxLon: 0,
          minLat: 0,
          minLon: 0,
          zoom: 0,
        },
    filters: {
      kind: {
        grouping: FilterGroupingEnum.And,
        predicate: { eq: GeojsonFeatureKind.Point },
      },
    },
  };

  const siteFilters: SiteFilterInputObject[] = [];
  if (searchQuery) {
    siteFilters.push(
      ...createSearchFilters(searchQuery, [
        { fieldName: 'areasCode' },
        { fieldName: 'areasName' },
        { fieldName: 'code' },
        { fieldName: 'name' },
        { fieldName: 'userFullName' },
        { fieldName: 'sectorName' },
      ]),
    );
  }
  if (additionalSiteFilters?.length) {
    siteFilters.push(...additionalSiteFilters);
  }
  if (siteFilters.length) {
    sitesQueryVariables.siteFilters = siteFilters;
  }
  const query = useSiteLocationsQuery({
    fetchPolicy,
    variables: sitesQueryVariables,
    skip,
  });

  const { loading, data, previousData, error, refetch } = query;
  const queryData = usePrevious ? data || previousData : data;
  const firstLoading = loading && previousData === undefined;
  const dataCollection = _get(
    queryData,
    'data',
    {},
  ) as GeojsonFeatureCollection;
  const metadata = _get(queryData, 'data.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 0);

  return {
    collection: [],
    data: dataCollection,
    error,
    firstLoading,
    loading,
    refetch,
    totalCount,
    totalPages,
  };
}
